import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Snowflake } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { CrystalModelResult, SolutePreset } from './solutionsLab';

/** A 2D particle beaker: dots are dissolved particles, diamonds are solid crystals. */
export function SolutionBeaker({
  label,
  temperature,
  dissolvedDots,
  crystalDots,
}: {
  label: string;
  temperature: number;
  dissolvedDots: number;
  crystalDots: number;
}) {
  return (
    <div className="min-w-0 text-center">
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">{label}</p>
      <div
        className="relative mx-auto mt-3 h-56 w-40 overflow-hidden rounded-b-[2.75rem] border-x-4 border-b-4 border-brand-sky/30 bg-white"
        role="img"
        aria-label={`${label} at ${temperature} degrees Celsius with ${crystalDots > 0 ? 'visible crystals' : 'no visible crystals'}`}
      >
        <div className="absolute inset-x-1 bottom-1 h-[78%] rounded-b-[2.4rem] bg-gradient-to-b from-brand-sky/15 to-brand-sky/35" />
        <div className="absolute inset-x-1 bottom-[78%] border-t-2 border-brand-sky/25" />
        {Array.from({ length: dissolvedDots }, (_, index) => (
          <span
            key={`dissolved-${index}`}
            className="absolute h-2.5 w-2.5 rounded-full bg-brand-sky"
            style={{
              left: `${15 + ((index * 29) % 70)}%`,
              top: `${30 + ((index * 19) % 46)}%`,
            }}
            aria-hidden="true"
          />
        ))}
        {Array.from({ length: crystalDots }, (_, index) => (
          <span
            key={`crystal-${index}`}
            className="absolute bottom-3 h-4 w-4 rotate-45 border-2 border-brand-coral bg-wash-sunshine"
            style={{ left: `${14 + ((index * 23) % 68)}%` }}
            aria-hidden="true"
          />
        ))}
      </div>
      <p className="mt-3 text-sm font-extrabold text-ink">{temperature} °C</p>
    </div>
  );
}

export function CrystalObservation({
  preset,
  model,
  mass,
  hotTemperature,
  coolTemperature,
}: {
  preset: SolutePreset;
  model: CrystalModelResult;
  mass: number;
  hotTemperature: number;
  coolTemperature: number;
}) {
  const [answer, setAnswer] = useState('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('CrystalObservation', { answer, checked });

  useEffect(() => {
    setAnswer('');
    setChecked(false);
  }, [coolTemperature, hotTemperature, mass, preset.id]);

  const numericAnswer = Number(answer);
  const correct =
    answer.trim().length > 0 &&
    Number.isFinite(numericAnswer) &&
    Math.abs(numericAnswer - model.crystalsOnCoolingG) <= 0.15;
  const hotDots = Math.max(5, Math.min(18, Math.round((model.dissolvedHotG / Math.max(1, mass)) * 18)));
  const coolDots = Math.max(4, Math.min(18, Math.round((Math.max(0, model.dissolvedHotG - model.crystalsOnCoolingG) / Math.max(1, mass)) * 18)));
  const crystalDots = model.crystalsOnCoolingG < 0.1
    ? 0
    : Math.max(3, Math.min(10, Math.round(model.crystalsOnCoolingG / 8)));

  return (
    <section className="mt-8 rounded-[1.75rem] border border-brand-mint/35 bg-gradient-to-br from-wash-mint via-white to-wash-sky p-5 sm:p-6">
      <div className="grid gap-7 lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] lg:items-center">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
            Watch the particles leave the solution
          </p>
          <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <SolutionBeaker
              label="Hot solution"
              temperature={hotTemperature}
              dissolvedDots={hotDots}
              crystalDots={Math.max(0, Math.round((mass - model.dissolvedHotG) / 10))}
            />
            <div className="flex flex-col items-center gap-2 text-brand-coral">
              <Snowflake className="h-7 w-7" aria-hidden="true" />
              <span className="text-xs font-extrabold">cool</span>
            </div>
            <SolutionBeaker
              label="After cooling"
              temperature={coolTemperature}
              dissolvedDots={coolDots}
              crystalDots={crystalDots}
            />
          </div>
          <p className="mt-4 text-center text-xs font-medium leading-5 text-slate2">
            Dots represent dissolved {preset.formula} particles. Diamonds represent solid crystals;
            their count is illustrative, while the mass calculation is deterministic.
          </p>
        </div>

        <div className="rounded-3xl border border-white bg-white/85 p-5 shadow-card-soft">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Calculate the crystal mass
          </p>
          <p className="mt-3 text-sm font-medium leading-6 text-ink-soft">
            Use the amount dissolved while hot and subtract the amount that can remain dissolved
            after cooling.
          </p>
          <p className="mt-4 rounded-2xl bg-surface px-4 py-3 text-center font-mono text-sm font-black text-ink">
            dissolved hot − cool limit = crystals
          </p>
          <label className="mt-4 block text-sm font-extrabold text-ink" htmlFor="crystal-mass-answer">
            Crystals formed
          </label>
          <div className="mt-2 flex items-center gap-2 rounded-2xl border border-hairline bg-white px-4 py-3 focus-within:border-brand-sky">
            <input
              id="crystal-mass-answer"
              type="number"
              min="0"
              step="0.1"
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setChecked(false);
              }}
              className="min-w-0 flex-1 bg-transparent text-lg font-extrabold text-ink outline-none"
              placeholder="0.0"
            />
            <span className="text-xs font-extrabold text-slate2">g</span>
          </div>
          <button
            type="button"
            disabled={!answer}
            onClick={() => setChecked(true)}
            className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Check crystal mass
          </button>
          {checked ? (
            <div
              role="status"
              className={`mt-4 rounded-2xl border p-4 ${
                correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'
              }`}
            >
              <p className="flex items-center gap-2 font-extrabold text-ink">
                {correct ? <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" /> : null}
                {correct ? 'Calculation complete' : 'Use the two limits, not the original mass twice'}
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                {model.dissolvedHotG.toFixed(1)} g − {model.coolLimitG.toFixed(1)} g ={' '}
                <strong>{model.crystalsOnCoolingG.toFixed(1)} g</strong>
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

